import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ChronoUnit, Duration, LocalDate } from "@js-joda/core";
import { motion } from "framer-motion";
import React from "react";
import { PatientTaskBroadcastDetails, PatientTaskInstanceId } from "../../../messages";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { translate } from "../../../translate";
import { formatDuration } from "../../../utils/dateUtils";
import { getColor } from "../../../utils/generateColor";
import { formatTaskAddress } from "../../../utils/taskUtils";
import {
  getStatusColor,
  getStatusText,
  getTaskStatus,
  TaskStatus,
} from "../../AvailableVisitsAndTasksList";
import { calcGeoDistance } from "../../DistanceLabel";
import { metersToMilesRounded } from "../../VisitListComponents";

type Props = {
  caregiver: Pick<AppCaregiver, "addressGeoLocation">;
  task: PatientTaskBroadcastDetails;
  onClick: (taskInstanceId: PatientTaskInstanceId) => void;
};

function TaskMapListedCard(props: Props, ref: React.Ref<HTMLDivElement>) {
  const [propertiesRef, setPropertiesRef] = React.useState<HTMLDivElement | null>(null);

  const {
    dueDate,
    title,
    durationMinutes,
    patientInitials,
    patientAddressComponents,
    patientAddress,
  } = props.task;

  const address = formatTaskAddress({
    addressString: patientAddress,
    addressComponents: patientAddressComponents,
  });
  const isTaskOverdue = dueDate.isBefore(LocalDate.now());

  return (
    <Root ref={ref} onClick={() => props.onClick(props.task.taskInstanceId)}>
      <Header>
        <Avatar>{patientInitials}</Avatar>
        <HeaderContent>
          <Title>{title}</Title>
          {address !== null && <Address>{address}</Address>}
        </HeaderContent>
        {(isTaskOverdue || isInitialAssessment(title)) && (
          <TaskOverdueWrapper>
            <TaskOverdue>{translate(STRINGS.URGENT)}</TaskOverdue>
          </TaskOverdueWrapper>
        )}
      </Header>
      <TaskFooter>
        <TaskProperties>
          <TaskPropertiesContainer
            isScrollable={
              propertiesRef ? propertiesRef.scrollWidth === propertiesRef.clientWidth : false
            }
            ref={setPropertiesRef}
            transition={{
              duration: 3,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "mirror",
              repeatDelay: 2,
            }}
            animate={{
              x: [
                "0px",
                propertiesRef
                  ? `-${propertiesRef.scrollWidth - propertiesRef.clientWidth}px`
                  : "0px",
              ],
            }}
          >
            <TaskProperty>{translate(STRINGS.TASK)}</TaskProperty>
            {durationMinutes !== null && (
              <TaskProperty>
                {formatDuration(Duration.of(durationMinutes, ChronoUnit.MINUTES))}
              </TaskProperty>
            )}
            {patientAddressComponents !== null && (
              <TaskProperty>
                {metersToMilesRounded(
                  calcGeoDistance(
                    props.caregiver.addressGeoLocation,
                    patientAddressComponents.location,
                    "K"
                  ) * 1000
                )}{" "}
                {translate(STRINGS.MILES_SHORT)}
              </TaskProperty>
            )}
          </TaskPropertiesContainer>
        </TaskProperties>
        <TaskMapListedCardStatus task={props.task} />
      </TaskFooter>
    </Root>
  );
}

export default React.forwardRef(TaskMapListedCard);

function isInitialAssessment(title: string) {
  return title.toLowerCase() === "initial assessment";
}

const Root = styled(motion.div)`
  background: white;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 10px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Avatar = styled.div<{ children: string }>`
  --size: 40px;
  background-color: ${({ children }) => getColor(children)};
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  color: white;
  width: var(--size);
  height: var(--size);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  border: 4px solid white;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
`;

const Address = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--tw-slate-500);
`;

const TaskOverdueWrapper = styled.div`
  margin-inline-start: auto;
  align-self: stretch;
  padding-top: 4px;
`;

const TaskOverdue = styled.div`
  font-size: 14px;
  color: var(--tw-red-600);
  background-color: var(--tw-red-50);
  border-radius: 2px;
  padding: 4px 8px;
  font-weight: 600;
`;

const TaskProperty = styled.span`
  font-weight: 700;
  font-size: 11px;
  line-height: 20px;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
  color: #475569;
`;

const TaskProperties = styled.div`
  background-color: var(--tw-slate-50);
  border-radius: 8px;
  padding: 6px 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const TaskPropertiesContainer = styled(motion.div)<{ isScrollable: boolean }>`
  display: flex;
  width: 100%;

  ${(props) => {
    if (props.isScrollable) {
      return css`
        align-items: center;
        justify-content: center;
      `;
    }

    return css``;
  }}

  ${TaskProperty} + ${TaskProperty} {
    &::before {
      content: "";
      display: inline-block;
      width: 1px;
      height: 5px;
      border-left: 1px solid var(--tw-slate-300);
      margin-inline-start: 12px;
      padding-inline-start: 12px;
    }
  }
`;

function TaskMapListedCardStatus(props: { task: PatientTaskBroadcastDetails }) {
  const status = getTaskStatus(props.task);
  const statusAsText = getStatusText(status).split(" ")[0];

  return <Root2 status={status}>{statusAsText}</Root2>;
}

const Root2 = styled.div<{ status: TaskStatus }>`
  --bg-color: black;
  --text-color: white;
  background-color: var(--tw-brand-secondary-500);
  color: var(--text-color);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  padding: 6px 8px 7px;
  text-transform: capitalize;
  flex-shrink: 0;

  display: flex;

  ${(props) => {
    switch (getStatusColor(props.status)) {
      case "blue":
        return css`
          --bg-color: var(--tw-blue-600);
          --text-color: var(--tw-blue-50);
        `;
      case "yellow":
        return css`
          --bg-color: var(--tw-amber-50);
          --text-color: var(--tw-amber-600);
        `;
      case "green":
        return css`
          --bg-color: var(--tw-emerald-50);
          --text-color: var(--tw-emerald-600);
        `;
      case "slate":
        return css`
          --bg-color: var(--tw-slate-100);
          --text-color: var(--tw-slate-600);
        `;
    }
  }}
`;

const TaskFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
`;
